// IntroJourney.tsx — the rebuilt pre-sign-in run (2026-09 onboarding deck).
//
// Six screens before the app asks anything of anyone:
//   0  splash      "Welcome to your renurturing journey" over the gold splash artwork
//   1  escape      what this is for
//   2  meets you   how it works
//   3  trust       why it can be trusted
//   4  built for   the five stars and a survivor's words
//   5  helped      what users say it does, and the price framing
//
// It replaces SoftEntry on iOS only (config/features ONBOARDING_2026). SoftEntry
// stays exactly as it is for web until this has been tested on device, so this file
// deliberately duplicates rather than edits it.
//
// Three things carry the tone and are not decoration:
//   * The artwork arrives SLOWLY. Each screen's image fades up over ~1.8s after it
//     decodes, at the opacity the deck specifies, so the screen settles rather than
//     snaps. Someone opening this in a bad hour meets a page that is already calm.
//   * There is a quiet Skip on every screen, the splash included, and the whole
//     screen is the tap target. An app that says nothing is expected of you must not
//     make anyone tap through six screens to prove it — and this run REPEATS until
//     an account exists (see App.tsx), so a returning visitor especially needs the
//     one-tap way past it.
//   * The run fades ITSELF out before handing over (EXIT_MS). The account screen sits
//     on the same cream paper, so what a user sees is one surface whose words change,
//     not two screens swapping.
//
// Nothing here is remembered across launches. Whether the run has been seen is App's
// business, and the answer it wants is "not until they have signed up".
import React from 'react';

/**
 * How long the run takes to clear itself before the account screen is asked for.
 * Long enough to read as a departure, short enough that a skip still feels instant.
 */
const EXIT_MS = 440;

/** The splash artwork takes its time arriving: this is the app's first breath. */
const SPLASH_FADE_MS = 2600;

const UI = 'Inter, system-ui, -apple-system, sans-serif';
const SERIF = 'Fraunces, Georgia, serif';
const IMG = '/newbuttonimages/';

// ── content ──────────────────────────────────────────────────────────────────
// Lines carry **bold** for the phrases the deck highlights; '' is a deliberate
// breath between stanzas. Heading and lines are separate so the screen can reveal
// a line at a time rather than arriving as a block.
type Kind = 'text' | 'quote' | 'proof';
interface Screen {
  kind: Kind;
  /**
   * Background artwork, full-bleed behind the text, at the deck's stated opacity.
   * The one exception is the last screen, which the deck puts at 25% but which has
   * the densest artwork of the five directly behind its text: it is dialled back
   * further so the words stay readable. Nudge it there, not here.
   */
  image?: string;
  opacity?: number;
  heading: string[];
  lines?: string[];
}

const SCREENS: Screen[] = [
  {
    kind: 'text',
    image: 'askrenurture.png',
    opacity: 0.35,
    heading: ['Escape the trauma of toxic relationships'],
    lines: [
      'Controlling and/or abusive past relationships leave **emotional scars that can last for years**.',
      'We provide moment to moment tools, alongside a long-term recovery programme, **to enable your recovery**.',
    ],
  },
  {
    kind: 'text',
    image: 'today_permission-to-do-less-overlay.png',
    opacity: 0.35,
    heading: ['An app that meets you, as you arrive'],
    lines: [
      '**Tell us how you are arriving**; panicked, dealing with your ex, needing compassion... ready to do the longer work.',
      '**We provide what you need**, in that moment.',
    ],
  },
  {
    kind: 'text',
    image: 'today_steadyyourselfbox.png',
    opacity: 0.25,
    heading: ['Why you can trust renurture?'],
    lines: [
      '**Designed by a Clinical Psychologist,** expert in relationship trauma.',
      '**Developed over 8 years**, with hundreds of survivors of relationship trauma.',
      '**Evidence-based** approaches, made **useful and flexible around your life**.',
    ],
  },
  {
    // The artwork here is a band rather than a wash, and it sits at full strength:
    // it separates the stars from the quote instead of tinting the whole page.
    kind: 'quote',
    image: 'today_continueprogramme.png',
    opacity: 1,
    heading: ['Built for you', 'With others, like you'],
    lines: [
      '“Renurture took me on the most self-exploring journey that I’ve ever been on in my life... that I have a value, I have self-worth, and I will never give that up again.“',
    ],
  },
  {
    // Below the deck's 25%: this is the one screen whose artwork sits directly under
    // the text rather than around it, and at 25% the ticks were hard to read.
    kind: 'proof',
    image: 'triage_feeling-unsafe-overlay.png',
    opacity: 0.14,
    heading: ['How our users say they are helped'],
    lines: [
      'improved mood',
      'more in control of life',
      'understanding the past',
      'useful at times of need',
    ],
  },
];

// ── bits ─────────────────────────────────────────────────────────────────────

/** Renders **bold** runs inside a line. Content stays readable in the array above. */
function rich(line: string): React.ReactNode[] {
  return line.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i} style={{ fontWeight: 700, color: 'var(--ink, #2c2a25)' }}>{part.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{part}</React.Fragment>,
  );
}

/**
 * The soft arrival. The image is mounted transparent and only fades to its target
 * opacity once the browser has decoded it, so it never half-paints or pops in.
 * `key`ed by src upstream so each screen runs its own arrival.
 *
 * Two details the artwork needs, both because of how these files are drawn:
 *   * They are landscape with the painting in the RIGHT half and white to the left,
 *     so a plain centre crop into a portrait phone shows almost nothing. 78% pulls
 *     the painted part into frame on every one of them.
 *   * The white is real white, not transparency. Over cream it would wash a pale
 *     rectangle across the screen, so it is composited with `multiply`: white leaves
 *     the paper untouched and only the pigment tints it.
 */
function SoftImage({ src, opacity, style, fadeMs = 1800 }: { src: string; opacity: number; style?: React.CSSProperties; fadeMs?: number }) {
  const [ready, setReady] = React.useState(false);
  return (
    <img
      src={IMG + src}
      alt=""
      aria-hidden="true"
      decoding="async"
      onLoad={() => setReady(true)}
      onError={() => setReady(true)}
      style={{
        opacity: ready ? opacity : 0,
        transition: `opacity ${fadeMs}ms cubic-bezier(0.22,0.61,0.36,1)`,
        pointerEvents: 'none',
        objectPosition: '78% 50%',
        mixBlendMode: 'multiply',
        ...style,
      }}
    />
  );
}

function Star({ size = 26 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ display: 'block' }}>
      <path
        d="M12 2.6l2.86 5.79 6.39.93-4.62 4.5 1.09 6.36L12 17.18l-5.72 3-1.09-6.36L.57 9.32l6.39-.93L12 2.6z"
        fill="#F0B429"
      />
    </svg>
  );
}

// ── screen ───────────────────────────────────────────────────────────────────

export function IntroJourney({ onDone }: { onDone: () => void }) {
  // -1 is the splash; 0..n index into SCREENS.
  const [i, setI] = React.useState(-1);
  const splash = i === -1;
  const screen = splash ? null : SCREENS[i];
  const last = i === SCREENS.length - 1;

  // Leaving: the run has been finished or skipped and is fading itself out. It stays
  // mounted through the fade, so nothing is asked of the user in the meantime.
  const [leaving, setLeaving] = React.useState(false);
  const finish = () => setLeaving(true);
  const advance = () => { if (leaving) return; if (last) finish(); else setI((n) => n + 1); };

  React.useEffect(() => {
    if (!leaving) return;
    const t = window.setTimeout(onDone, EXIT_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaving]);

  // Warm the next screen's artwork while this one is being read, so the soft arrival
  // is a choice rather than a wait on a slow connection.
  React.useEffect(() => {
    const next = SCREENS[i + 1];
    if (next?.image) { const p = new Image(); p.src = IMG + next.image; }
  }, [i]);

  // The paper: cream, full height, and it never animates. Whatever replaces this
  // component sits on the same colour, so the background is continuous across the
  // hand-off and only the contents change.
  const paper: React.CSSProperties = {
    position: 'relative', width: '100%', minHeight: '100dvh', maxWidth: 520, margin: '0 auto',
    background: 'linear-gradient(178deg, #fbf6ed 0%, var(--cream, #f7f1e6) 42%, #f2e9d9 100%)',
    color: 'var(--ink, #2c2a25)',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
  };

  // The sheet: everything that is actually drawn, including the artwork. This is what
  // lifts and fades on the way out.
  const sheet: React.CSSProperties = {
    position: 'relative', flex: 1, display: 'flex', flexDirection: 'column',
    padding: 'calc(var(--safe-top) + 26px) 26px calc(var(--safe-bottom) + 30px)',
    opacity: leaving ? 0 : 1,
    transform: leaving ? 'translateY(-12px)' : 'none',
    transition: `opacity ${EXIT_MS}ms ease, transform ${EXIT_MS + 140}ms cubic-bezier(0.22,0.61,0.36,1)`,
  };

  /** Quiet, top-right, on every screen including the splash. */
  const skip = (
    <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end' }}>
      <button
        onClick={(e) => { e.stopPropagation(); finish(); }}
        style={{
          border: 'none', background: 'none', cursor: 'pointer', padding: '6px 4px',
          fontFamily: UI, fontSize: 13, fontWeight: 400, letterSpacing: '0.02em',
          color: 'var(--ink-soft, #5a554c)', opacity: 0.55,
        }}
      >
        Skip
      </button>
    </div>
  );

  // ── splash ────────────────────────────────────────────────────────────────
  // The artwork IS the screen: the brand mark painted large, bleeding off every edge,
  // with its own pale cream at the top where the words go. It loads slowly and alone
  // (SPLASH_FADE_MS), and the words settle onto it afterwards rather than waiting on
  // it — small, low-contrast, unhurried.
  //
  // Tap anywhere, exactly like every screen after it, with the same quiet word at the
  // bottom instead of a button. A solid CTA across the foot of a full-bleed painting
  // fights it; and there is no real decision here to justify the weight.
  if (splash) {
    return (
      <div onClick={() => setI(0)} style={paper}>
        {/* Not composited with `multiply` like the wash screens: this file is already
            painted on the app's own cream, so it is simply the page. */}
        <SoftImage
          src="renurturesplashscreen.webp"
          opacity={1}
          fadeMs={SPLASH_FADE_MS}
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: '50% 50%', mixBlendMode: 'normal',
          }}
        />
        <div style={sheet}>
          {skip}

          <div style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '11vh' }}>
            <p
              style={{
                margin: 0, fontFamily: UI, fontSize: 14.5, fontWeight: 400,
                letterSpacing: '0.05em', color: 'var(--ink-soft, #5a554c)', opacity: 0.72,
                animation: 'fadeUp 1100ms cubic-bezier(0.22,0.61,0.36,1) both', animationDelay: '900ms',
              }}
            >
              Welcome to your
            </p>
            {/* House style: the emphasis word in the serif italic, in amber. Set light
                and generously spaced so it sits ON the painting rather than over it. */}
            <h1
              style={{
                margin: '10px 0 0', fontFamily: SERIF, fontSize: 25, fontWeight: 300,
                lineHeight: 1.3, letterSpacing: '0.005em', color: 'var(--ink, #2c2a25)',
                textAlign: 'center',
                animation: 'fadeUp 1100ms cubic-bezier(0.22,0.61,0.36,1) both', animationDelay: '1250ms',
              }}
            >
              <em style={{ fontStyle: 'italic', color: 'var(--amber-deep, #9b6712)' }}>renurturing</em> journey
            </h1>
          </div>

          {/* The same affordance the rest of the run uses: a word, not a button. A
              little more present than the others only because it lands on the painted
              gold rather than on bare cream. */}
          <div
            style={{
              position: 'relative', textAlign: 'center', fontFamily: UI, fontSize: 13.5,
              color: 'var(--ink, #2c2a25)', opacity: 0.6,
              animation: 'fade 1200ms ease both', animationDelay: '1700ms',
            }}
          >
            Start
          </div>
        </div>
      </div>
    );
  }

  const s = screen as Screen;
  const centred = s.kind !== 'text';

  return (
    <div onClick={advance} style={paper}>
      <div style={sheet}>
        {/* Full-bleed wash for the two-thirds of screens that carry one. The quote
            screen's artwork is a band instead, drawn inside the content below. */}
        {s.image && s.kind !== 'quote' && (
          <SoftImage
            key={s.image}
            src={s.image}
            opacity={s.opacity ?? 0.3}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}

        {skip}

        {/* key={i} restarts the reveal on every screen, so each one arrives the same way. */}
        <div
          key={i}
          style={{
            position: 'relative', flex: 1, display: 'flex', flexDirection: 'column',
            justifyContent: 'center', paddingBottom: 18, textAlign: centred ? 'center' : 'left',
          }}
        >
          <h1
            style={{
              fontFamily: UI, fontSize: centred ? 24 : 25, fontWeight: 700, lineHeight: 1.28,
              letterSpacing: '-0.3px', margin: 0,
              // A measure on the centred headings so they break evenly rather than
              // dropping a single word onto the second line.
              ...(centred ? { maxWidth: 250, marginLeft: 'auto', marginRight: 'auto' } : null),
              animation: 'fadeUp 700ms cubic-bezier(0.22,0.61,0.36,1) both',
            }}
          >
            {s.heading.map((h, n) => (
              <React.Fragment key={n}>{n > 0 && <br />}{h}</React.Fragment>
            ))}
          </h1>

          {s.kind === 'text' && (
            <div style={{ marginTop: 20 }}>
              {(s.lines ?? []).map((line, n) =>
                line === '' ? (
                  <div key={n} style={{ height: 14 }} />
                ) : (
                  <p
                    key={n}
                    style={{
                      fontFamily: UI, fontSize: 15.5, lineHeight: 1.58, color: 'var(--ink-soft, #5a554c)',
                      margin: 0,
                      animation: 'fadeUp 700ms cubic-bezier(0.22,0.61,0.36,1) both',
                      animationDelay: `${Math.min(180 + n * 150, 1200)}ms`,
                    }}
                  >
                    {rich(line)}
                  </p>
                ),
              )}
            </div>
          )}

          {s.kind === 'quote' && (
            <>
              <div
                style={{
                  display: 'flex', justifyContent: 'center', gap: 6, marginTop: 22,
                  animation: 'fadeUp 700ms cubic-bezier(0.22,0.61,0.36,1) both', animationDelay: '200ms',
                }}
              >
                {[0, 1, 2, 3, 4].map((n) => <Star key={n} />)}
              </div>

              {/* The band: a rule of light between the stars and the words, and it has
                  to REACH BOTH EDGES. The source file is a wide horizon with a third
                  of empty paper on its left, so showing it whole leaves the band
                  starting a third of the way in. Cropping to the painted part
                  (`cover`, anchored right, in a band deep enough to force the crop)
                  is what fills the screen edge to edge. */}
              {/* flexShrink: 0 matters — this is a flex item in a centred column, and
                  without it the band gets squeezed and the wave squashes to a thread.
                  104px is the height at which the crop below lands on source x 375..
                  1024, i.e. the painted part exactly filling a 390pt screen. */}
              <div style={{ margin: '22px -26px 0', height: 104, flexShrink: 0, overflow: 'hidden' }}>
                {s.image && (
                  <SoftImage
                    key={s.image}
                    src={s.image}
                    opacity={s.opacity ?? 1}
                    style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', objectPosition: '100% 50%' }}
                  />
                )}
              </div>

              <p
                style={{
                  fontFamily: UI, fontSize: 15.5, fontWeight: 600, fontStyle: 'italic', lineHeight: 1.55,
                  color: 'var(--ink, #2c2a25)', margin: '24px auto 0', maxWidth: 320,
                  animation: 'fadeUp 700ms cubic-bezier(0.22,0.61,0.36,1) both', animationDelay: '420ms',
                }}
              >
                {(s.lines ?? [])[0]}
              </p>
              <p
                style={{
                  fontFamily: UI, fontSize: 15, fontWeight: 700, margin: '20px 0 0', color: 'var(--ink, #2c2a25)',
                  animation: 'fade 700ms ease both', animationDelay: '640ms',
                }}
              >
                C.P.
              </p>
            </>
          )}

          {s.kind === 'proof' && (
            <>
              {/* Centred as a block, but left-aligned inside it: a ragged column of
                  ticks reads as a mistake, not as a list. */}
              <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 14, alignItems: 'flex-start', margin: '28px auto 0' }}>
                {(s.lines ?? []).map((line, n) => (
                  <div
                    key={n}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      animation: 'fadeUp 700ms cubic-bezier(0.22,0.61,0.36,1) both',
                      animationDelay: `${180 + n * 130}ms`,
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ flex: '0 0 auto' }}>
                      <path d="M4.5 12.8l4.6 4.7L19.5 6.6" fill="none" stroke="#2fa36a" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span style={{ fontFamily: UI, fontSize: 15.5, fontWeight: 600, color: 'var(--ink, #2c2a25)' }}>{line}</span>
                  </div>
                ))}
              </div>

              <p
                style={{
                  fontFamily: UI, fontSize: 17, fontWeight: 700, lineHeight: 1.4, margin: '38px 0 0',
                  animation: 'fadeUp 700ms cubic-bezier(0.22,0.61,0.36,1) both', animationDelay: '760ms',
                }}
              >
                Here for you, 24/7
              </p>
              <p
                style={{
                  fontFamily: UI, fontSize: 17, fontWeight: 700, lineHeight: 1.4, margin: '20px 0 0',
                  animation: 'fadeUp 700ms cubic-bezier(0.22,0.61,0.36,1) both', animationDelay: '880ms',
                }}
              >
                at 5% of the price<br />of weekly therapy
              </p>
            </>
          )}
        </div>

        {/* Where you are, without turning it into a form to complete: dots, no counter. */}
        <div style={{ position: 'relative', display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 20 }}>
          {SCREENS.map((_, n) => (
            <span
              key={n}
              style={{
                width: 5, height: 5, borderRadius: 999,
                background: 'var(--ink, #2c2a25)', opacity: n === i ? 0.38 : 0.13,
                transition: 'opacity 400ms ease',
              }}
            />
          ))}
        </div>

        {/* One affordance the whole way through: this run never pushes, and the last
            screen hands over to the account screen, which does its own asking. */}
        <div
          style={{
            position: 'relative', textAlign: 'center', fontFamily: UI, fontSize: 13.5,
            color: 'var(--ink-soft, #5a554c)', opacity: 0.5,
            animation: 'fade 900ms ease both', animationDelay: '700ms',
          }}
        >
          Continue
        </div>
      </div>
    </div>
  );
}
